import { ExternalLink, Menu as MenuIcon, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import MobileNavigation from './MobileNavigation';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ApplicationsClosedDialog from './ApplicationsClosedDialog';

interface SubMenuItem {
  id: string;
  label: string;
  url: string;
}

interface MenuItem {
  id: string;
  label: string;
  submenu?: SubMenuItem[];
}

interface Menu {
  items: MenuItem[];
}

interface NavbarProps {
  backgroundColor?: string;
}

const Navbar = ({ backgroundColor = 'bg-transparent' }: NavbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when clicking a link
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Handle navigation with hash
  const handleNavigation = (hash: string) => {
    if (location.pathname !== '/') {
      navigate(`/${hash}`);
    } else {
      // If we're already on the home page, update URL and scroll to the section
      window.history.pushState(null, '', hash);
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Add effect to handle hash navigation after page load
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          // Small delay to ensure the page is fully loaded
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    // Handle initial load
    handleHashNavigation();

    // Handle hash changes
    window.addEventListener('hashchange', handleHashNavigation);
    return () => window.removeEventListener('hashchange', handleHashNavigation);
  }, []);

  // Define the menu structure
  const navMenu: Menu = {
    items: [
      {
        id: 'main-menu',
        label: 'Navigation',
        submenu: [
          { id: 'about', label: 'About', url: '#about' },
          { id: 'partners', label: 'Partners', url: '#partners' },
          { id: 'speakers', label: 'Speakers', url: '#speakers' },
          { id: 'prizes', label: 'Prizes', url: '#prizes' },
          { id: 'about-us', label: 'Organisers', url: '#about-us' },
          //{ id: 'projects', label: 'Projects', url: '/projects/2025' },
          //{ id: 'projects', label: 'Projects', url: '/projects/2025' },
          {
            id: 'github',
            label: 'GitHub',
            url: 'https://github.com/CDTM/cdtm-hacks',
          },
        ],
      },
    ],
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-white/90 backdrop-blur-sm shadow-sm`}
    >
      <div className="w-full px-4 py-4 flex items-center justify-between max-w-none">
        {/* Logo */}
        <Link to="/" className="font-bold text-xl text-springBlue">
          <img
            src="/images/CDTM_Hacks_Logo.svg"
            alt="CDTM Valencia Hacks Logo"
            className="h-8 md:h-10 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <button
            onClick={() => handleNavigation('#about')}
            className="text-springText/80 hover:text-springBlue transition-colors"
          >
            About
          </button>
          <button
            onClick={() => handleNavigation('#partners')}
            className="text-springText/80 hover:text-springBlue transition-colors"
          >
            Partners
          </button>
          <button
            onClick={() => handleNavigation('#speakers')}
            className="text-springText/80 hover:text-springBlue transition-colors"
          >
            Speakers
          </button>
          <button
            onClick={() => handleNavigation('#prizes')}
            className="text-springText/80 hover:text-springBlue transition-colors"
          >
            Prizes
          </button>
          {/*
          <button
            onClick={() => handleNavigation('#schedule')}
            className="text-springText/80 hover:text-springBlue transition-colors"
          >
            Schedule
          </button>
          */}
          <button
            onClick={() => handleNavigation('#about-us')}
            className="text-springText/80 hover:text-springBlue transition-colors flex items-center gap-2"
          >
            Organisers
          </button>

          {/*<a
            href="/projects/2025"
            className="text-springText/80 hover:text-springBlue transition-colors flex items-center gap-2"
          >
            Projects
          </a>*/}
          <a
            href="https://github.com/CDTM/cdtm-hacks"
            target="_blank"
            rel="noopener noreferrer"
            className="text-springText/80 hover:text-springBlue transition-colors flex items-center gap-1"
          >
            GitHub
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden flex items-center justify-center p-2 rounded-lg bg-white shadow-sm"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="text-springBlue" size={24} />
          ) : (
            <MenuIcon className="text-springBlue" size={24} />
          )}
        </button>
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation
        menu={navMenu}
        onItemClick={handleLinkClick}
        isOpen={isMenuOpen}
      />

      {/* Applications Closed Dialog */}
      <ApplicationsClosedDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </header>
  );
};

export default Navbar;
